/**
 * Kameleoon AB Testing Helpers
 * -----------------------------------------------------------------------------
 * Utility functions for working with Kameleoon experiments.
 *
 */

/* eslint-disable no-undef */

/**
 * Wait for Kameleoon to load and check if user is in specific experiment variation
 * @param {Object} options - Configuration options
 * @param {Number} options.experimentId - Kameleoon experiment ID
 * @param {Number} [options.variationId] - Specific variation ID to check (optional)
 * @param {Function} options.onReady - Callback when Kameleoon is ready
 * @param {Number} [options.timeout=5000] - Maximum wait time in milliseconds
 * @param {Number} [options.interval=100] - Check interval in milliseconds
 * @returns {Function} Cleanup function to stop waiting
 *
 * @example
 * // Wait for specific variation
 * const cleanup = waitForKameleoon({
 *   experimentId: 351859,
 *   variationId: 1214332,
 *   onReady: (isInVariation) => {
 *     if (isInVariation) {
 *       // User is in test variant
 *     } else {
 *       // User is in control or other variant
 *     }
 *   }
 * });
 *
 * @example
 * // Just check if experiment is active
 * waitForKameleoon({
 *   experimentId: 351859,
 *   onReady: (experiment) => {
 *     const isActive = experiment?.active ?? experiment?.activatedInVisit;
 *     const variationId = experiment?.associatedVariation?.id;
 *     // Handle experiment state
 *   }
 * });
 */
export function waitForKameleoon({
  experimentId,
  variationId = null,
  onReady,
  timeout = 5000,
  interval = 100,
}) {
  let attempts = 0;
  const maxAttempts = timeout / interval;

  const checkInterval = setInterval(() => {
    attempts++;

    const experiment =
      globalThis.Kameleoon?.API?.Experiments?.getById(experimentId);

    // Stop if Kameleoon loaded OR max wait time reached
    if (experiment || attempts >= maxAttempts) {
      clearInterval(checkInterval);

      if (variationId === null) {
        // Return full experiment object
        onReady(experiment);
      } else {
        // Check if user is in specific variation
        const isActive = experiment?.active ?? experiment?.activatedInVisit;
        const userIsInVariation =
          experiment?.associatedVariation?.id === variationId && isActive;
        onReady(userIsInVariation, experiment);
      }
    }
  }, interval);

  // Return cleanup function
  return () => clearInterval(checkInterval);
}

/**
 * Check if user is in specific Kameleoon experiment variation (synchronous)
 * Note: When no experiment object is provided, this only works if Kameleoon is already loaded.
 * @param {Number} experimentId - Kameleoon experiment ID
 * @param {Number} variationId - Specific variation ID to check
 * @param {Object} [experiment] - Optional: Pre-fetched experiment object (for performance)
 * @returns {Boolean} True if user is in the specified variation
 *
 * @example
 * // Using experiment ID (fetches experiment)
 * if (isInVariation(351859, 1214332)) {
 *   // User is in test variant
 * }
 *
 * @example
 * // Using pre-fetched experiment object (better performance)
 * const experiment = getExperiment(351859);
 * if (isInVariation(351859, 1214332, experiment)) {
 *   // User is in test variant (no additional fetch)
 * }
 *
 * @example
 * // In component context with isESN00Test wrapper
 * isESN00Test() {
 *   return isInVariation(351859, 1214332);
 * }
 */
export function isInVariation(experimentId, variationId, experiment = null) {
  // Use provided experiment or fetch it
  const exp =
    experiment || globalThis.Kameleoon?.API?.Experiments?.getById(experimentId);

  if (!exp) {
    return false;
  }

  const isActive = exp?.active ?? exp?.activatedInVisit;
  return Boolean(exp?.associatedVariation?.id === variationId && isActive);
}

/**
 * Get Kameleoon experiment information (synchronous)
 * Note: Only works if Kameleoon is already loaded
 * @param {Number} experimentId - Kameleoon experiment ID
 * @returns {Object|null} Experiment object or null if not loaded
 *
 * @example
 * const experiment = getExperiment(351859);
 * if (experiment) {
 *   const variationId = experiment.associatedVariation?.id;
 *   const isActive = experiment.active ?? experiment.activatedInVisit;
 * }
 */
export function getExperiment(experimentId) {
  return globalThis.Kameleoon?.API?.Experiments?.getById(experimentId) || null;
}

/**
 * Trigger a Kameleoon custom event
 * @param {String} eventName - Name of the custom event to trigger
 *
 * @example
 * triggerKameleoonEvent('view_cart');
 */
export function triggerKameleoonEvent(eventName) {
  // Validate event name before queuing
  if (typeof eventName !== 'string' || eventName.trim().length === 0) {
    return false;
  }

  globalThis.kameleoonQueue = globalThis.kameleoonQueue || [];
  globalThis.kameleoonQueue.push(['Events.trigger', eventName]);
  return true;
}

/**
 * Trigger a Kameleoon custom goal
 * @param {Number} goalId - numeric ID of the goal
 */
export function triggerKameleoonGoal(goalId) {
  // Validate goal id before queuing
  if (typeof goalId !== 'number') {
    return false;
  }

  globalThis.kameleoonQueue = globalThis.kameleoonQueue || [];
  globalThis.kameleoonQueue.push(['Goals.processConversion', goalId]);
  return true;
}

// Tracks which "once per page view" custom events have already fired, so
// multiple component instances (e.g. several product cards on one page)
// don't push the same event to the queue redundantly.
const sentOnceEvents = new Set();

/**
 * Trigger a Kameleoon custom event, but only the first time it's called
 * for a given event name during this page view.
 * @param {String} eventName - Name of the custom event to trigger
 * @returns {Boolean} True if the event was sent (first call), false if it
 * was already sent this page view or the event name is invalid.
 *
 * @example
 * triggerKameleoonEventOnce('esn74-pdp-price-portion');
 */
export function triggerKameleoonEventOnce(eventName) {
  if (sentOnceEvents.has(eventName)) {
    return false;
  }

  const sent = triggerKameleoonEvent(eventName);
  if (sent) {
    sentOnceEvents.add(eventName);
  }

  return sent;
}
